import update from "immutability-helper";
import { useCallback, useState, useRef, useEffect } from "react";
import { useDrop } from "react-dnd";
import { Table } from "./Table.js";
import { ItemTypes } from "./ItemTypes.js";
import { snapToGrid as doSnapToGrid } from "./snapToGrid.js";
import DraggableTable from "./DraggableTable.js";
import { OrangeButton, CloseIcon } from "../components/DefaultImports.js";
import GridItems from "./GridItems.js";
import styled from "styled-components";
import {
  Nav,
  Card,
  Button,
  Row,
  Col,
  Modal,
  Container,
  Form,
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";

// ⬇️ ACTIONS
import {
  createTables,
  fetchTables,
  createPlaceOfTable,
  fetchPlaceOfTable,
  bulkUpdateTables, // bulk patch thunk
} from "../actions/restaurantActions";

// ⬇️ API client for DELETE endpoints
import { api } from "../App";

const StyledDiv = styled.div`
  width: 100%;
  max-height: 400px;
  overflow: auto;
  border-radius: 0 0 5px 5px;

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
`;

const StyledDivInside = styled.div`
  width: 750px;
  height: 400px;
  position: relative;
  background-color: white;
  background-image: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.04) 1px,
      transparent 1px
    ),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.04) 1px, transparent 1px);
  background-size: 150px 150px;
`;

const StyledCard = styled(Card)`
  margin: 10px 0;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
  border-radius: 0 0 5px 5px;
  overflow: hidden;
`;

const CardTop = styled.div`
  padding: 5px;
  background-color: #faf6e9;
  position: relative;
`;

const CardMenuTop = styled.div`
  height: auto;
  padding: 5px 5px 0 5px;
  background-color: #faf6e9;
  position: relative;
`;

const CardBot = styled.div`
  height: 100px;
  background-color: white;
  position: relative;
`;

const CardBotGrid = styled.div`
  height: 35px;
  background-color: #faf6e9;
  width: 100%;
  position: relative;
  padding: 5px;
`;

const StyledSelect = styled.select`
  width: 85%;
  height: 1.5rem;
  font-size: 1rem;
  border: none;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

/* aligned dashed add box */
const AddFilterBox = styled.button`
  width: 100%;
  padding: 12px 14px;
  border: 1px dashed #99b0ad;
  border-radius: 8px;
  background: #ffffff;
  color: #99b0ad;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s ease;

  &:hover {
    border-color: #ee754c;
    color: #ee754c;
  }
`;

export const Grid = ({
  snapToGrid,
  selectedItem,
  setSelectedItem,
  setWidth,
  setHeight,
  all_tables,
  setDraggingItem,
  draggingItem,
  restaurant_id, // optional; we read from store anyway
}) => {
  const dispatch = useDispatch();

  const [selectedPlace, setSelectedPlace] = useState(false);
  const [selectedSeparator, setSelectedSeparator] = useState("Tables");
  const [tabOrPills, setTabOrPills] = useState("tabs");
  const [topDistance, setTopDistance] = useState("");
  const [isReady, setIsReady] = useState(false);

  const containerRef = useRef(null);
  const insideContainerRef = useRef(null);
  const MenuRef = useRef(null);

  const [tables, setTables] = useState([]);

  const restaurantZones = useSelector((state) => state.restaurantZones);
  const { zones = [] } = restaurantZones;

  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails;

  const [canCreate, setCanCreate] = useState(false);

  const [objects, setObjects] = useState([
    {
      height: 50,
      id: 246,
      isPlaced: false,
      left: 0,
      number_of_seats: "3",
      place_of_table: 7,
      restaurant: 1,
      round_or_square: "square",
      table_name: "1",
      table_number: "1",
      top: 0,
      type: "table",
      width: 50,
    },
  ]);

  // ⬇️ Modals: create/delete tables
  const [showCreateTables, setShowCreateTables] = useState(false);
  const [numTablesToAdd, setNumTablesToAdd] = useState("");
  const [numSeatsPerTable, setNumSeatsPerTable] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [showDeleteTables, setShowDeleteTables] = useState(false);
  const [numTablesToDelete, setNumTablesToDelete] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // ⬇️ Modals: create/delete zone (room)
  const [showCreateZone, setShowCreateZone] = useState(false);
  const [zoneName, setZoneName] = useState("");
  const [isCreatingZone, setIsCreatingZone] = useState(false);

  const [showDeleteZone, setShowDeleteZone] = useState(false);
  const [isDeletingZone, setIsDeletingZone] = useState(false);

  // ⬇️ Bulk editor modal
  const [showBulkEditor, setShowBulkEditor] = useState(false);
  const [bulkEditPlace, setBulkEditPlace] = useState(null);
  const [bulkRows, setBulkRows] = useState([]);
  const [isBulkEditorSaving, setIsBulkEditorSaving] = useState(false);

  // ⬇️ In-modal create/delete state
  const [modalAddCount, setModalAddCount] = useState("");
  const [modalAddSeats, setModalAddSeats] = useState("");
  const [modalDelCount, setModalDelCount] = useState("");
  const [modalIsCreating, setModalIsCreating] = useState(false);
  const [modalIsDeleting, setModalIsDeleting] = useState(false);

  // ⬇️ Single-row delete state + confirmation modal
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    tableId: null,
    label: "",
  });

  // Derived
  useEffect(() => {
    setCanCreate(
      !!restaurants?.id &&
        !!selectedPlace &&
        Number(numTablesToAdd) > 0 &&
        Number(numSeatsPerTable) > 0
    );
  }, [numTablesToAdd, numSeatsPerTable, selectedPlace, restaurants?.id]);

  useEffect(() => {
    setTables(all_tables);
  }, [all_tables]);

  useEffect(() => {
    if (zones.length > 0 && !selectedPlace) setSelectedPlace(zones[0].id);
  }, [zones]);

  useEffect(() => {
    if (selectedItem && tables[selectedItem]) {
      const width = tables[selectedItem].width;
      const height = tables[selectedItem].height;
      console.log(
        `Selected Table ID: ${selectedItem}, Width: ${width}, Height: ${height}`
      );
    }
  }, [selectedItem, tables]);

  // keep bulkRows in sync while modal is open
  useEffect(() => {
    if (showBulkEditor && bulkEditPlace) {
      const rows = (tables || [])
        .filter((t) => t.place_of_table === bulkEditPlace)
        .sort((a, b) => Number(a.table_number) - Number(b.table_number));
      setBulkRows(rows.map((r) => ({ ...r })));
    }
  }, [tables, showBulkEditor, bulkEditPlace]);

  const selectItem = (value) => setSelectedItem(value);

  const moveTable = useCallback((id, left, top, state) => {
    setTables((tables) =>
      tables.map((table) => {
        if (table.id === id) {
          return {
            ...table,
            left: state === "tableOff" ? 20 : left,
            top: state === "tableOff" ? 20 : top,
            isPlaced: state !== "tableOff" && state !== "reset",
          };
        }
        return table;
      })
    );
  }, []);

  const [, drop] = useDrop(
    () => ({
      accept: ItemTypes.TABLE,
      drop(item, monitor) {
        const state = item.state;
        const delta = monitor.getDifferenceFromInitialOffset();
        let left = Math.round(item.left + delta.x);
        let top = Math.round(item.top + delta.y);
        if (snapToGrid) [left, top] = doSnapToGrid(left, top);
        moveTable(item.id, left, top, state);
        return undefined;
      },
    }),
    [moveTable, snapToGrid]
  );

  useEffect(() => {
    drop(containerRef);
  }, [drop, containerRef, isReady]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width < 297) setTabOrPills("pills");
      else setTabOrPills("tabs");
    });
    if (MenuRef.current) resizeObserver.observe(MenuRef.current);
    return () => {
      if (MenuRef.current) resizeObserver.unobserve(MenuRef.current);
    };
  }, [isReady]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setIsReady(true), 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (insideContainerRef.current) {
        const rect = insideContainerRef.current.getBoundingClientRect();
        const topDistance = insideContainerRef.current.scrollTop;
        const top = rect.top;
        setTopDistance(top);
      }
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target) {
          const rect = entry.target.getBoundingClientRect();
          const top = rect.top;
          setTopDistance(top);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      window.addEventListener("scroll", handleScroll);
      if (typeof containerRef.current.addEventListener === "function") {
        containerRef.current.addEventListener("scroll", handleScroll);
      }
    }

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", handleScroll);
      if (
        containerRef.current &&
        typeof containerRef.current.removeEventListener === "function"
      ) {
        containerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [containerRef, insideContainerRef, isReady]);

  // ---- Handlers: Tables
  const handleCreateTables = async () => {
    if (!canCreate) return;
    try {
      setIsCreating(true);
      const payload = {
        place_of_table: selectedPlace,
        num_tables_to_create: Number(numTablesToAdd),
        num_seats: Number(numSeatsPerTable),
      };
      await dispatch(createTables(restaurants.id, payload));
      await dispatch(fetchTables(restaurants.id));
      setShowCreateTables(false);
      setNumTablesToAdd("");
      setNumSeatsPerTable("");
    } catch (e) {
      console.error("Failed to create tables:", e);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTables = async () => {
    const qty = Number(numTablesToDelete);
    if (!restaurants?.id || !selectedPlace || !qty || qty < 1) return;
    try {
      setIsDeleting(true);
      await api.delete(
        `restaurants/${restaurants.id}/zones/${selectedPlace}/delete-tables/${qty}/`
      );
      await dispatch(fetchTables(restaurants.id));
      setShowDeleteTables(false);
      setNumTablesToDelete("");
    } catch (e) {
      console.error("Failed to delete tables:", e);
    } finally {
      setIsDeleting(false);
    }
  };

  // ---- Handlers: Zones
  const handleCreateZone = async () => {
    if (!restaurants?.id || !zoneName?.trim()) return;
    try {
      setIsCreatingZone(true);
      const payload = {
        restaurant: restaurants.id,
        place_of_table: zoneName.trim(),
      };
      await dispatch(createPlaceOfTable(restaurants.id, payload));
      await dispatch(fetchPlaceOfTable(restaurants.id));
      setShowCreateZone(false);
      setZoneName("");
    } catch (e) {
      console.error("Failed to create zone:", e);
    } finally {
      setIsCreatingZone(false);
    }
  };

  const handleDeleteZone = async () => {
    if (!restaurants?.id || !selectedPlace) return;
    try {
      setIsDeletingZone(true);
      await api.delete(
        `restaurants/${restaurants.id}/placeoftable/${selectedPlace}/`
      );
      setSelectedPlace(false);
      setShowDeleteZone(false);
      await dispatch(fetchPlaceOfTable(restaurants.id));
      await dispatch(fetchTables(restaurants.id));
    } catch (e) {
      console.error("Failed to delete zone:", e);
    } finally {
      setIsDeletingZone(false);
    }
  };

  // ---- BULK SAVE: Guardar (save ALL current tables)
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const pickUpdateFields = (t) => {
    const u = { id: t.id };
    if (typeof t.table_name !== "undefined") u.table_name = t.table_name;
    if (typeof t.number_of_seats !== "undefined")
      u.number_of_seats = t.number_of_seats;
    if (typeof t.can_connect_tables !== "undefined")
      u.can_connect_tables = t.can_connect_tables || "";
    if (typeof t.left !== "undefined") u.left = t.left;
    if (typeof t.top !== "undefined") u.top = t.top;
    if (typeof t.width !== "undefined") u.width = t.width;
    if (typeof t.height !== "undefined") u.height = t.height;
    if (typeof t.isPlaced !== "undefined") u.isPlaced = !!t.isPlaced;
    if (typeof t.round_or_square !== "undefined")
      u.round_or_square = t.round_or_square;
    return u;
  };

  const handleSaveAllTables = async () => {
    if (!restaurants?.id || !Array.isArray(tables) || tables.length === 0)
      return;
    try {
      setIsBulkSaving(true);
      const updates = tables.map(pickUpdateFields);
      await dispatch(bulkUpdateTables(restaurants.id, updates));
      await dispatch(fetchTables(restaurants.id));
    } catch (e) {
      console.error("Bulk save failed:", e);
    } finally {
      setIsBulkSaving(false);
    }
  };

  // ---- BULK EDIT MODAL (list view per room)
  const openBulkEditor = () => {
    const initialPlace = selectedPlace || (zones[0]?.id ?? null);
    setBulkEditPlace(initialPlace);
    const rows = (tables || [])
      .filter((t) => t.place_of_table === initialPlace)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));
    setBulkRows(rows.map((r) => ({ ...r })));
    setShowBulkEditor(true);
    setModalAddCount("");
    setModalAddSeats("");
    setModalDelCount("");
  };

  const changeBulkRoom = (placeId) => {
    setBulkEditPlace(placeId);
    const rows = (tables || [])
      .filter((t) => t.place_of_table === placeId)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));
    setBulkRows(rows.map((r) => ({ ...r })));
    setModalAddCount("");
    setModalAddSeats("");
    setModalDelCount("");
  };

  const onBulkRowChange = (id, field, value) => {
    setBulkRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const saveBulkEditor = async () => {
    if (!restaurants?.id) return;
    try {
      setIsBulkEditorSaving(true);
      const updates = bulkRows.map((r) => {
        const u = { id: r.id };
        if (typeof r.table_name !== "undefined") u.table_name = r.table_name;
        if (typeof r.number_of_seats !== "undefined")
          u.number_of_seats = Number(r.number_of_seats) || 0;
        if (typeof r.round_or_square !== "undefined")
          u.round_or_square = r.round_or_square || "square";
        return u;
      });

      await dispatch(bulkUpdateTables(restaurants.id, updates));
      await dispatch(fetchTables(restaurants.id));
      changeBulkRoom(bulkEditPlace);
    } catch (e) {
      console.error("Bulk editor save failed:", e);
    } finally {
      setIsBulkEditorSaving(false);
      setShowBulkEditor(false);
    }
  };

  // ---- NEW: Delete a single table (inside bulk editor)
  const handleDeleteSingleTable = async (tableId) => {
    if (!restaurants?.id || !tableId) return;
    try {
      setDeletingRowId(tableId);
      await api.delete(`restaurants/${restaurants.id}/tables/${tableId}/`);
      await dispatch(fetchTables(restaurants.id));
      // list will refresh via the useEffect that watches `tables`
    } catch (e) {
      console.error("Single table delete failed:", e);
    } finally {
      setDeletingRowId(null);
    }
  };

  // ---- In-modal Create/Delete for current room ----
  const handleModalCreateTables = async () => {
    const cnt = Number(modalAddCount);
    const seats = Number(modalAddSeats);
    if (
      !restaurants?.id ||
      !bulkEditPlace ||
      !cnt ||
      cnt < 1 ||
      !seats ||
      seats < 1
    )
      return;
    try {
      setModalIsCreating(true);
      const payload = {
        place_of_table: bulkEditPlace,
        num_tables_to_create: cnt,
        num_seats: seats,
      };
      await dispatch(createTables(restaurants.id, payload));
      await dispatch(fetchTables(restaurants.id));
      // keep list open and auto-refresh via tables -> useEffect
      setModalAddCount("");
      setModalAddSeats("");
    } catch (e) {
      console.error("Modal create failed:", e);
    } finally {
      setModalIsCreating(false);
    }
  };

  const handleModalDeleteTables = async () => {
    const qty = Number(modalDelCount);
    if (!restaurants?.id || !bulkEditPlace || !qty || qty < 1) return;
    try {
      setModalIsDeleting(true);
      await api.delete(
        `restaurants/${restaurants.id}/zones/${bulkEditPlace}/delete-tables/${qty}/`
      );
      await dispatch(fetchTables(restaurants.id));
      setModalDelCount("");
    } catch (e) {
      console.error("Modal delete failed:", e);
    } finally {
      setModalIsDeleting(false);
    }
  };

  // Derived helpers
  const tablesInSelectedZone = (tables || []).filter(
    (t) => t.place_of_table === selectedPlace
  ).length;
  const canOpenDeleteTables =
    !!restaurants?.id && !!selectedPlace && tablesInSelectedZone > 0;

  return (
    <Container className="no-select">
      {/* ----- TOP CARD (rooms dropdown + create/delete room buttons) ----- */}
      <StyledCard>
        <CardTop>
          <Row className="align-items-center">
            <Col xs={6} sm={6} md={8}></Col>
            <Col xs={6} sm={6} md={4}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <StyledSelect
                  value={selectedPlace || ""}
                  onChange={(e) =>
                    setSelectedPlace(parseInt(e.target.value, 10))
                  }
                >
                  {zones.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.place_of_table}
                    </option>
                  ))}
                </StyledSelect>

                {/* Nova sala */}
                <OrangeButton
                  size="sm"
                  style={{ fontSize: "0.8rem", padding: "3px 8px" }}
                  onClick={() => setShowCreateZone(true)}
                  disabled={!restaurants?.id}
                  title={
                    !restaurants?.id ? "Restaurante inválido" : "Nova sala"
                  }
                >
                  Nova sala
                </OrangeButton>

                {/* Eliminar sala */}
                <Button
                  variant="danger"
                  size="sm"
                  style={{ fontSize: "0.8rem", padding: "3px 8px" }}
                  onClick={() => setShowDeleteZone(true)}
                  disabled={!selectedPlace}
                  title={
                    !selectedPlace ? "Selecione uma sala" : "Eliminar sala"
                  }
                >
                  Eliminar sala
                </Button>
              </div>
            </Col>
          </Row>
        </CardTop>

        {tables && tables.length > 0 && (
          <StyledDiv ref={containerRef}>
            <StyledDivInside ref={insideContainerRef}>
              {tables
                .filter((table) => table.place_of_table === selectedPlace)
                .map((table, index) => (
                  <DraggableTable
                    key={index}
                    id={table.id}
                    {...table}
                    selectedItem={selectedItem}
                    selectItem={selectItem}
                    tables={tables}
                    moveTable={moveTable}
                    table={table}
                  />
                ))}
            </StyledDivInside>
          </StyledDiv>
        )}

        <CardBotGrid>
          <Row>
            <Col xs={6} sm={6} md={8}></Col>
            <Col xs={6} sm={6} md={4}>
              <ActionButtons>
                <OrangeButton
                  size="sm"
                  className="mr-2"
                  style={{ fontSize: "0.8rem", padding: "3px" }}
                  onClick={handleSaveAllTables}
                  disabled={isBulkSaving || !restaurants?.id || !tables?.length}
                  title={
                    !restaurants?.id
                      ? "Restaurante inválido"
                      : "Guardar todas as mesas"
                  }
                >
                  {isBulkSaving ? "A guardar…" : "Guardar"}
                </OrangeButton>
              </ActionButtons>
            </Col>
          </Row>
        </CardBotGrid>
      </StyledCard>

      {/* ----- BOTTOM CARD (tabs + create/delete tables buttons) ----- */}
      <StyledCard>
        <CardMenuTop ref={MenuRef}>
          <Row className="align-items-center">
            <Col xs={7} sm={8} md={8}>
              <Nav variant={tabOrPills} defaultActiveKey="link-0">
                <Nav.Item>
                  <Nav.Link
                    onClick={() => setSelectedSeparator("Tables")}
                    eventKey="link-0"
                  >
                    Mesa
                  </Nav.Link>
                </Nav.Item>
                {/*                 <Nav.Item>
                  <Nav.Link
                    onClick={() => setSelectedSeparator("Obj")}
                    eventKey="link-1"
                  >
                    Objectos
                  </Nav.Link>
                </Nav.Item> */}
              </Nav>
            </Col>
            <Col xs={5} sm={4} md={4}>
              <ActionButtons>
                {/* Bulk editor toggle */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  style={{ fontSize: "0.8rem", padding: "3px" }}
                  onClick={openBulkEditor}
                  disabled={!selectedPlace}
                  title={
                    !selectedPlace
                      ? "Selecione uma sala para editar"
                      : "Editar lista de mesas"
                  }
                >
                  Editar lista
                </Button>

                <OrangeButton
                  size="sm"
                  style={{ fontSize: "0.8rem", padding: "3px" }}
                  onClick={() => setShowCreateTables(true)}
                  disabled={!selectedPlace}
                  title={
                    !selectedPlace
                      ? "Selecione uma sala para criar mesas"
                      : "Adicionar mesas"
                  }
                >
                  Adicionar mesas
                </OrangeButton>

                {/*                 <Button
                  variant="danger"
                  size="sm"
                  style={{ fontSize: "0.8rem", padding: "3px" }}
                  onClick={() => setShowDeleteTables(true)}
                  disabled={!canOpenDeleteTables}
                  title={
                    !canOpenDeleteTables
                      ? "Sem mesas na sala selecionada"
                      : "Eliminar mesas"
                  }
                >
                  Eliminar mesas
                </Button> */}
              </ActionButtons>
            </Col>
          </Row>
        </CardMenuTop>

        <CardBot>
          {tables && tables.length > 0 && (
            <GridItems
              items={tables}
              topDistance={topDistance}
              moveTable={(id, left, top, state) =>
                moveTable(id, left, top, state)
              }
              selectItem={selectItem}
              selectedPlace={selectedPlace}
              selectedSeparator={selectedSeparator}
              objects={objects}
            />
          )}
        </CardBot>
      </StyledCard>

      {/* ---- Modal: Create Tables (outside) ---- */}
      <Modal
        show={showCreateTables}
        onHide={() => setShowCreateTables(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Adicionar mesas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="numTablesToAdd">
              <Form.Label>Número de mesas a adicionar</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={numTablesToAdd}
                onChange={(e) => setNumTablesToAdd(e.target.value)}
                placeholder="Ex.: 6"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="numSeatsPerTable">
              <Form.Label>Número de lugares por mesa</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={numSeatsPerTable}
                onChange={(e) => setNumSeatsPerTable(e.target.value)}
                placeholder="Ex.: 4"
              />
            </Form.Group>

            <div style={{ fontSize: "0.85rem", color: "#666" }}>
              Posteriormente podes alterar o número de lugares de cada mesa no
              botão "Editar mesas".
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateTables(false)}
            disabled={isCreating}
          >
            Cancelar
          </Button>
          <OrangeButton
            onClick={handleCreateTables}
            disabled={!canCreate || isCreating}
          >
            {isCreating ? "A criar…" : "Guardar"}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      {/* ---- Modal: Delete Tables (outside) ---- */}
      <Modal
        show={showDeleteTables}
        onHide={() => setShowDeleteTables(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar mesas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2" style={{ fontSize: "0.9rem", color: "#555" }}>
            Sala selecionada:{" "}
            <b>
              {zones.find((z) => z.id === selectedPlace)?.place_of_table || "-"}
            </b>
            <br />
            Mesas actuais nesta sala:{" "}
            <b>
              {
                (tables || []).filter((t) => t.place_of_table === selectedPlace)
                  .length
              }
            </b>
          </div>
          <Form>
            <Form.Group className="mb-3" controlId="numTablesToDelete">
              <Form.Label>Número de mesas a eliminar</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={numTablesToDelete}
                onChange={(e) => setNumTablesToDelete(e.target.value)}
                placeholder="Ex.: 1"
              />
              <Form.Text muted>
                Serão removidas as últimas N mesas da sala.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteTables(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteTables}
            disabled={
              isDeleting ||
              !restaurants?.id ||
              !selectedPlace ||
              !Number(numTablesToDelete) ||
              Number(numTablesToDelete) < 1
            }
          >
            {isDeleting ? "A eliminar…" : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- Modal: Create Zone ---- */}
      <Modal
        show={showCreateZone}
        onHide={() => setShowCreateZone(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Nova sala</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="zoneName">
              <Form.Label>Nome da sala</Form.Label>
              <Form.Control
                type="text"
                value={zoneName}
                onChange={(e) => setZoneName(e.target.value)}
                placeholder="Ex.: Sala Principal"
              />
            </Form.Group>
            <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 8 }}>
              Será criada uma nova sala para este restaurante.
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateZone(false)}
            disabled={isCreatingZone}
          >
            Cancelar
          </Button>
          <OrangeButton
            onClick={handleCreateZone}
            disabled={isCreatingZone || !zoneName.trim()}
          >
            {isCreatingZone ? "A criar…" : "Guardar"}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      {/* ---- Modal: Delete Zone ---- */}
      <Modal
        show={showDeleteZone}
        onHide={() => setShowDeleteZone(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar sala</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: "0.95rem" }}>
            Tem a certeza que pretende eliminar a sala{" "}
            <b>
              {zones.find((z) => z.id === selectedPlace)?.place_of_table || "-"}
            </b>
            ?
            <br />
            Mesas nesta sala:{" "}
            <b>
              {
                (tables || []).filter((t) => t.place_of_table === selectedPlace)
                  .length
              }
            </b>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 8 }}>
            Esta ação não pode ser desfeita.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteZone(false)}
            disabled={isDeletingZone}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteZone}
            disabled={isDeletingZone || !selectedPlace}
          >
            {isDeletingZone ? "A eliminar…" : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- Modal: Bulk Edit List (per-row delete + dashed add button) ---- */}
      <Modal
        show={showBulkEditor}
        onHide={() => setShowBulkEditor(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar lista de mesas</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Room selector only */}
          <div style={{ marginBottom: 12 }}>
            <Form.Group
              controlId="bulkEditRoom"
              style={{ margin: 0, maxWidth: 320 }}
            >
              <Form.Label>Sala</Form.Label>
              <Form.Select
                value={bulkEditPlace || ""}
                onChange={(e) => changeBulkRoom(parseInt(e.target.value, 10))}
              >
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.place_of_table}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          {/* Header row with labels */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(60px, 90px) 1fr 160px 160px 90px",
              gap: 10,
              fontWeight: 700,
              padding: "6px 8px",
              borderBottom: "1px solid #e9ecef",
              background: "#fafafa",
              borderTopLeftRadius: 6,
              borderTopRightRadius: 6,
            }}
            role="row"
            aria-label="Cabeçalhos da lista de mesas"
          >
            <div>Mesa</div>
            <div>Nome da mesa</div>
            <div>Lugares</div>
            <div>Formato</div>
            <div>Eliminar mesa</div>
          </div>

          {/* Table list */}
          {bulkRows.length === 0 ? (
            <div
              style={{
                color: "#666",
                padding: "10px 8px",
                border: "1px solid #eee",
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
              }}
            >
              Sem mesas nesta sala.
            </div>
          ) : (
            <div
              style={{
                maxHeight: 420,
                overflowY: "auto",
                border: "1px solid #eee",
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
                padding: 8,
              }}
              role="table"
              aria-label="Lista de mesas editáveis"
            >
              {bulkRows.map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(60px, 90px) 1fr 160px 160px 90px",
                    gap: 10,
                    alignItems: "center",
                    padding: "6px 8px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  role="row"
                >
                  {/* Mesa (número - read-only) */}
                  <div style={{ fontWeight: 600 }}>
                    {row.table_number || "-"}
                  </div>

                  {/* Nome */}
                  <Form.Group style={{ margin: 0 }}>
                    <Form.Label visuallyHidden>Nome</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.table_name || ""}
                      onChange={(e) =>
                        onBulkRowChange(row.id, "table_name", e.target.value)
                      }
                      placeholder="Nome da mesa"
                      aria-label={`Nome da mesa ${row.table_number || ""}`}
                    />
                  </Form.Group>

                  {/* Lugares */}
                  <Form.Group style={{ margin: 0 }}>
                    <Form.Label visuallyHidden>Lugares</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={row.number_of_seats ?? ""}
                      onChange={(e) =>
                        onBulkRowChange(
                          row.id,
                          "number_of_seats",
                          e.target.value
                        )
                      }
                      placeholder="Lugares"
                      aria-label={`Lugares da mesa ${row.table_number || ""}`}
                    />
                  </Form.Group>

                  {/* Formato */}
                  <Form.Group style={{ margin: 0 }}>
                    <Form.Label visuallyHidden>Formato</Form.Label>
                    <Form.Select
                      value={row.round_or_square || "square"}
                      onChange={(e) =>
                        onBulkRowChange(
                          row.id,
                          "round_or_square",
                          e.target.value
                        )
                      }
                      aria-label={`Formato da mesa ${row.table_number || ""}`}
                    >
                      <option value="square">Quadrada</option>
                      <option value="round">Redonda</option>
                    </Form.Select>
                  </Form.Group>

                  {/* Ações: open confirm modal for single delete */}
                  <div>
                    {/*                <Button
                      variant="danger"
                      size="sm"
                      onClick={() =>
                        setConfirmDelete({
                          show: true,
                          tableId: row.id,
                          label:
                            row.table_number || row.table_name || `#${row.id}`,
                        })
                      }
                      disabled={deletingRowId === row.id || isBulkEditorSaving}
                      title="Eliminar esta mesa"
                    >
                      {deletingRowId === row.id ? "A eliminar…" : "Eliminar"}
                    </Button> */}
                    <CloseIcon
                      style={{
                        color: "#99B0AD",
                        right: "0",
                        position: "relative",
                      }}
                      onClick={() =>
                        setConfirmDelete({
                          show: true,
                          tableId: row.id,
                          label:
                            row.table_number || row.table_name || `#${row.id}`,
                        })
                      }
                    />
                  </div>
                </div>
              ))}

              {/* Dashed add button under the list */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: 10,
                }}
              >
                <AddFilterBox
                  type="button"
                  onClick={() => setShowCreateTables(true)}
                  aria-label="Adicionar mesas"
                  title="Adicionar mesas"
                >
                  <span style={{ fontSize: 18, lineHeight: "18px" }}>＋</span>
                  <span>Adicionar mesas</span>
                </AddFilterBox>
              </div>
            </div>
          )}
        </Modal.Body>

        {/* Footer: only Cancel / Save edits */}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBulkEditor(false)}
            disabled={isBulkEditorSaving}
          >
            Cancelar
          </Button>
          <OrangeButton
            onClick={saveBulkEditor}
            disabled={isBulkEditorSaving || !bulkRows.length}
            title="Guardar alterações"
          >
            {isBulkEditorSaving ? "A guardar…" : "Guardar alterações"}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      {/* ---- Confirm single table delete ---- */}
      <Modal
        show={confirmDelete.show}
        onHide={() =>
          setConfirmDelete({ show: false, tableId: null, label: "" })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar mesa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tens a certeza que pretendes eliminar esta mesa?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setConfirmDelete({ show: false, tableId: null, label: "" })
            }
            disabled={deletingRowId === confirmDelete.tableId}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (!confirmDelete.tableId) return;
              await handleDeleteSingleTable(confirmDelete.tableId);
              setConfirmDelete({ show: false, tableId: null, label: "" });
            }}
            disabled={deletingRowId === confirmDelete.tableId}
          >
            {deletingRowId === confirmDelete.tableId
              ? "A eliminar…"
              : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
